/**
 * CommsPage — data container for the Mobile Forensics view.
 *
 * Owns which extraction and thread are open plus the chat paging; the list's
 * search/filter state lives in `ConversationList` itself. Lives under
 * `features/` because it value-imports react-query hooks, which the purity
 * rule keeps out of `src/components/**`.
 */

import { Loader2, SmartphoneIcon } from 'lucide-react'
import { type FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import type { ExcerptRange } from '@/components/comms/ChatThread'
import { type CommsTab, CommsView } from '@/components/comms/CommsView'
import { Card } from '@/components/ui/card'
import {
  useCommsActivity,
  useCommsConversations,
  useCommsExcerpts,
  useCommsExtractions,
  useCommsMessageSearch,
  useCommsMessages,
  useCommsNotifications,
  useCommsWebVisits,
  useMineExcerpt,
  usePromoteExcerpt,
} from '@/features/comms/hooks/use-comms'
import { useDebounced } from '@/hooks/use-debounced'
import type { CommsExcerpt, CommsRange } from '@/services/api/comms'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { excerptGraphView, landExcerptInObjectSet } from './excerpt-object-set'

const NoExtractions: FC = () => {
  const { t } = useTranslation()
  return (
    <Card className="mx-4 mt-4 flex max-w-md flex-col items-center gap-2 p-6 text-center md:mx-auto md:mt-10 md:p-8">
      <SmartphoneIcon className="size-8 text-muted-foreground" />
      <h2 className="text-sm font-semibold text-foreground">{t('comms.empty.title')}</h2>
      <p className="text-xs text-muted-foreground">{t('comms.empty.body')}</p>
    </Card>
  )
}

export const CommsPage: FC<{ caseId: string }> = ({ caseId }) => {
  const { t } = useTranslation()
  const extractionsQuery = useCommsExtractions(caseId)
  const extractions = extractionsQuery.data ?? []

  // The newest extraction is the default; state records only an explicit pick.
  const [pickedExtractionId, setPickedExtractionId] = useState<string | null>(null)
  const [conversationId, setConversationId] = useState<string | null>(null)
  // Set only when a thread is opened from a search hit — see `useCommsMessages`.
  const [anchor, setAnchor] = useState<string | null>(null)
  const [tab, setTab] = useState<CommsTab>('chats')
  // The list pane filters thread names off this string as you type; the
  // full-text scan behind it waits for you to stop.
  const [search, setSearch] = useState('')
  const [fuzzy, setFuzzy] = useState(false)
  // The window every parquet read is scoped to — set from presets or by
  // clicking a bar of the thread's activity histogram.
  const [range, setRange] = useState<CommsRange>({})
  const debouncedSearch = useDebounced(search, 275)
  const extractionId = pickedExtractionId ?? extractions[0]?.extractionId ?? null

  // Deep link back into the evidence (see components/comms/comms-links): a
  // MessageExcerpt or Conversation node, or an agent's window hit, links to
  // `?conversation=…` plus optionally `extraction=` (pins the device
  // extraction), `from=` (opens the thread at that instant instead of its
  // newest page) and `excerpt=` (lands on that promoted range, highlighted).
  const [searchParams, setSearchParams] = useSearchParams()
  const linkedConversation = searchParams.get('conversation')
  const linkedFrom = searchParams.get('from')
  const linkedExtraction = searchParams.get('extraction')
  const linkedExcerpt = searchParams.get('excerpt')
  // The promoted range the thread was opened on; cleared the moment the
  // analyst moves to another thread or extraction.
  const [focusExcerptId, setFocusExcerptId] = useState<string | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: consume the link once, then clear it
  useEffect(() => {
    if (!linkedConversation) return
    if (linkedExtraction) setPickedExtractionId(linkedExtraction)
    setConversationId(linkedConversation)
    setAnchor(linkedFrom)
    setFocusExcerptId(linkedExcerpt)
    setTab('chats')
    // Clear so a later in-app navigation isn't yanked back to the linked range.
    for (const key of ['conversation', 'from', 'extraction', 'excerpt']) searchParams.delete(key)
    setSearchParams(searchParams, { replace: true })
  }, [linkedConversation, linkedFrom, linkedExtraction, linkedExcerpt])

  const conversationsQuery = useCommsConversations(caseId, extractionId, range)
  const messagesQuery = useCommsMessages(caseId, extractionId, conversationId, anchor, range)
  const activityQuery = useCommsActivity(caseId, extractionId, conversationId)
  // The web-history query lazily follows the tab — no scan until the user opens it.
  const webVisitsQuery = useCommsWebVisits(caseId, tab === 'web' ? extractionId : null, range)
  const notificationsQuery = useCommsNotifications(
    caseId,
    tab === 'notifications' ? extractionId : null,
    range
  )
  const searchQuery = useCommsMessageSearch(
    caseId,
    tab === 'chats' ? extractionId : null,
    debouncedSearch,
    fuzzy,
    range
  )

  // Pages arrive newest-slice-first; each page is chronological inside, so
  // the full window is the pages reversed, flattened.
  const messages = useMemo(
    () => [...(messagesQuery.data?.pages ?? [])].reverse().flatMap(page => page.messages),
    [messagesQuery.data]
  )
  // Web visits are already newest-first per page; flatten in page order.
  const webPages = webVisitsQuery.data?.pages ?? []
  const webVisits = useMemo(() => webPages.flatMap(page => page.visits), [webPages])
  const webBrowsers = webPages[0]?.browsers ?? []
  // Notification events mirror the web visits; registered/apps ride page one.
  const notificationPages = notificationsQuery.data?.pages ?? []
  const notificationEvents = useMemo(
    () => notificationPages.flatMap(page => page.events),
    [notificationPages]
  )

  const selectExtraction = (id: string) => {
    setPickedExtractionId(id)
    setConversationId(null)
    setAnchor(null)
    setFocusExcerptId(null)
    // Hits belong to the extraction they were found in.
    setSearch('')
  }

  // Promotion is only meaningful where the acquisition captured message
  // bodies; at T0/T2 the affordance is replaced by the reason (SC-013).
  const activeExtraction = extractions.find(e => e.extractionId === extractionId)
  const promotable = !['T0', 'T2'].includes(activeExtraction?.tier ?? '')

  const excerptsQuery = useCommsExcerpts(caseId, extractionId, conversationId)
  const mine = useMineExcerpt(caseId, extractionId)
  const promote = usePromoteExcerpt(caseId, extractionId)

  /** Put the freshly promoted excerpt where the analyst is actually looking.
   *  Writing it to the case graph is not enough: the lens views render an
   *  object set, so without this the excerpt is real and invisible. */
  const land = (excerpt: CommsExcerpt) => {
    const conversation = conversationsQuery.data?.find(
      c => c.conversationId === excerpt.conversationId
    )
    const title = conversation?.displayName || excerpt.conversationId
    const view = excerptGraphView(excerpt, caseId, title)
    return landExcerptInObjectSet(
      getOrCreateInvestigationsStore({ caseId }),
      view,
      excerpt.note || title
    )
  }

  const promoteRange = (range: ExcerptRange) => {
    if (!conversationId) return
    promote.mutate(
      { conversationId, ...range },
      {
        onSuccess: excerpt => {
          const landing = land(excerpt)
          toast.success(
            landing === 'draft'
              ? t('comms.promote.doneDraft', {
                  count: excerpt.messageCount,
                })
              : t('comms.promote.doneActive', {
                  count: excerpt.messageCount,
                })
          )
        },
        onError: (error: Error) =>
          toast.error(
            t('comms.promote.failed', {
              msg: error.message,
            })
          ),
      }
    )
  }

  /** Split publish: one excerpt per sitting, in order, so a partial failure
   * leaves the excerpts that did land rather than an all-or-nothing gamble. */
  const promoteMany = async (ranges: ExcerptRange[]) => {
    if (!conversationId) return
    let published = 0
    try {
      for (const range of ranges) {
        land(await promote.mutateAsync({ conversationId, ...range }))
        published += 1
      }
      toast.success(
        t('comms.promote.doneMany', {
          count: published,
        })
      )
    } catch (error) {
      toast.error(
        t('comms.promote.failedPartial', {
          done: published,
          total: ranges.length,
          msg: (error as Error).message,
        })
      )
    }
  }

  if (extractionsQuery.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
      </div>
    )
  }
  if (extractions.length === 0) return <NoExtractions />

  return (
    <CommsView
      extractions={extractions}
      activeExtractionId={extractionId}
      onSelectExtraction={selectExtraction}
      tab={tab}
      onTab={setTab}
      conversations={conversationsQuery.data ?? []}
      activeConversationId={conversationId}
      onSelectConversation={id => {
        setAnchor(null)
        setFocusExcerptId(null)
        setConversationId(id)
      }}
      searchQuery={search}
      onSearchQuery={setSearch}
      fuzzy={fuzzy}
      onFuzzy={setFuzzy}
      messageMatches={searchQuery.data?.matches ?? []}
      matchesHasMore={searchQuery.data?.hasMore ?? false}
      matchesLoading={searchQuery.isFetching}
      onSelectMatch={message => {
        setAnchor(message.sentAt)
        setConversationId(message.conversationId)
      }}
      messages={messages}
      hasOlder={messagesQuery.hasNextPage ?? false}
      loadingOlder={messagesQuery.isFetchingNextPage || messagesQuery.isLoading}
      onLoadOlder={() => messagesQuery.fetchNextPage()}
      onPromote={promoteRange}
      onPromoteMany={promoteMany}
      promoting={promote.isPending}
      promotable={promotable}
      excerpts={excerptsQuery.data ?? []}
      focusExcerptId={focusExcerptId}
      mining={mine.isPending}
      onMine={excerpt =>
        mine.mutate(excerpt.excerptId, {
          onSuccess: result =>
            toast.success(
              t('comms.published.mined', {
                count: result.mined,
              })
            ),
          onError: (error: Error) =>
            toast.error(
              t('comms.published.mineFailed', {
                msg: error.message,
              })
            ),
        })
      }
      range={range}
      onRange={setRange}
      activity={activityQuery.data ?? []}
      notPromotableReason={t('comms.promote.tierBlocked')}
      webVisits={webVisits}
      webBrowsers={webBrowsers}
      webHasMore={webVisitsQuery.hasNextPage ?? false}
      webLoadingMore={webVisitsQuery.isFetchingNextPage || webVisitsQuery.isLoading}
      onWebLoadMore={() => webVisitsQuery.fetchNextPage()}
      notifications={{
        events: notificationEvents,
        registered: notificationPages[0]?.registered ?? [],
        apps: notificationPages[0]?.apps ?? [],
        hasMore: notificationsQuery.hasNextPage ?? false,
        loadingMore: notificationsQuery.isFetchingNextPage || notificationsQuery.isLoading,
        onLoadMore: () => notificationsQuery.fetchNextPage(),
      }}
    />
  )
}
